'use client'
import { type FC, useCallback } from 'react'
import type { CartItem, DeliveryDTO, ProductLightDTO } from 'ecosystem'
import { OverridesContextProvider, type OverridesContextType } from 'ui/lib/overrides'
import type { GrandSectionHeadingLink } from 'ui/storefront/layout/GrandSection'
import { FeaturedProducts } from '../components'
import { type IFeaturedProducts } from '../components/FeaturedProducts'

export type TagProductsWidgetBaseConfig = {
  itemCount?: number
  products: ProductLightDTO[]
  title: string
  onProductBuy: (item: CartItem) => void
  cartItems: CartItem[]
  onProductRequest: (item: ProductLightDTO) => void
  deliveryOptions: DeliveryDTO[] | null
  overrides?: OverridesContextType
  paths: {
    productsUrlPath: string
  }
  isLoading?: boolean
  headingLink?: GrandSectionHeadingLink
} & Pick<IFeaturedProducts, 'customComponents'>

interface TagProductsWidgetBaseProps {
  config: TagProductsWidgetBaseConfig
}

const TagProductsWidgetBase: FC<TagProductsWidgetBaseProps> = ({
  config: {
    overrides,
    itemCount = 10,
    onProductBuy,
    paths,
    products,
    title,
    cartItems,
    ...restConfig
  }
}) => {
  const handleAdd = useCallback(
    (product: ProductLightDTO) => {
      const itemInCart = cartItems.find((item) => item.id === product.id)

      const qty = (itemInCart?.qty ?? 0) + 1

      onProductBuy({
        id: product.id,
        type: 'SINGLE',
        qty,
        meta: {
          singleType: {
            product
          }
        }
      })
    },
    [cartItems, onProductBuy]
  )

  const limitedTagProducts = products.slice(0, itemCount)

  return (
    <OverridesContextProvider overrides={{ ...overrides }}>
      <FeaturedProducts
        items={limitedTagProducts}
        onBuy={handleAdd}
        productsUrlPath={paths.productsUrlPath}
        sectionId="products-tag"
        title={title || ''}
        carouselItemsByBreakpoint={{ sm: 1, md: 5, lg: 6 }}
        {...restConfig}
      />
    </OverridesContextProvider>
  )
}

export default TagProductsWidgetBase
